import React from "react";

import { css, useTheme } from "@emotion/react";

import { HeadFC, PageProps } from "gatsby";

import { center } from "../utils/global-styles";

import GridContainer from "@upsun/react-component-library/src/components/GridContainer";
import GridCol from "@upsun/react-component-library/src/components/GridCol";
import Heading from "@upsun/react-component-library/src/components/Heading";
import DesaturatedQuote from "@upsun/react-component-library/src/components/DesaturatedQuote";

import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/all";
import { mediaQueries } from "@upsun/react-component-library/src/styles/css-utils";
import Layout from "../components/Layout";
import Stacks from "../components/homepage/Stacks";
import { SEO } from "../components/Seo";
import HomepageHero from "../components/homepage/HomepageHero";
import InfoItemsHomepage from "../components/homepage/InfoItemsHomepage";
import themeSwitcher from "../utils/theme-utils";
import { UpsunTheme } from "@upsun/react-component-library/src/themes";
import colorsLight from "@upsun/react-component-library/src/styles/colors-light";
import colorsDark from "@upsun/react-component-library/src/styles/colors-dark";
import Button from "@upsun/react-component-library/src/components/Button";
import TrustedBanner from "../components/homepage/TrustedBanner";
import { LogoShowcase } from "../components/LogoShowcase";
import { homepageLogos } from "../data/homepage-data";
import { dropShadows, pxToRem, respSpacing } from "@upsun/react-component-library/src/styles";
import FeatureList from "../components/homepage/FeatureList";
import { spacing } from "@upsun/react-component-library/src/styles/css-utils";

import mizzouDark from "../../static/homepage/logos/dark/mizzou-dark.png";
import mizzouLight from "../../static/homepage/logos/light/mizzou-light.png";

import unicefLight from "../../static/homepage/logos/light/unicef-light.svg";
import unicefDark from "../../static/homepage/logos/dark/unicef-dark.svg";

import freitagDark from "../../static/homepage/logos/dark/freitag-dark.png";
import freitagLight from "../../static/homepage/logos/light/freitag-light.png";

const IndexPage: React.FC<PageProps> = ({ location }: PageProps) => {
  const theme = useTheme() as UpsunTheme;

  gsap.registerPlugin(useGSAP);
  gsap.registerPlugin(ScrollTrigger);

  useGSAP(() => {
    const matchMedia = gsap.matchMedia();
    matchMedia.add(
      {
        mobile: '(max-width: 767px)',
        tablet: '(min-width: 768px)',
        desktop: '(min-width: 1280px)'
      },
      (context) => {
        const { tablet } = context.conditions as gsap.Conditions;
        gsap.to(".slideDown", {
          scrollTrigger: {
            trigger: ".slideDown",
            scrub: 1
          },
          y: tablet ? 50 : 0
        });
        gsap.to(".slideUp", { 
          scrollTrigger: {
            trigger: ".slideUp",
            scrub: 1
          },
          y: tablet ? -50 : 0
        })
      }
    );
  },{dependencies: []});

  const card = css`
    position: relative;
    border-radius: 16px;
    text-align: center;
    height: 100%;

    padding: 32px;

    background: ${themeSwitcher(theme, {
      light: colorsLight.NeutralBgDefault,
      dark: colorsDark.NeutralBgDefault,
    })};

    ${mediaQueries.md} {
      padding: 40px;
    }

    ${mediaQueries.xl} {
      border-radius: 32px;
      padding: 48px;
    }
  `;

  const gradientBorder = css`
    &::before {
      content: "";
      position: absolute;
      top: ${pxToRem(-2)};
      bottom: ${pxToRem(-2)};
      left: ${pxToRem(-2)};
      right: ${pxToRem(-2)};
      border-radius: ${pxToRem(16)};
      z-index: -1;
      background-image: linear-gradient(240deg, #ed49f0 0%, #6046ff 100%);

      ${mediaQueries.xl} {
        border-radius: ${pxToRem(32)};
      }
    }

    &:focus-visible {
      outline: none;
    }

    &:visited {
      color: unset;
    }
  `;

  const greyBorder = css`
    border: 2px solid
      ${themeSwitcher(theme, {
        light: colorsLight.NeutralBorderWeakDisabled,
        dark: colorsDark.NeutralBorderWeakDisabled,
      })};
  `;

  const gradientBorderTransform = css`
    background: linear-gradient(
          ${themeSwitcher(theme, {
              light: colorsLight.NeutralBgDefault,
              dark: colorsDark.NeutralBgDefault,
            })}
            0 0
        )
        padding-box,
      /*this is your grey background*/
        linear-gradient(to right, #ed49f0, #6046ff) border-box;
    border: 2px solid transparent;
    border-radius: 16px;
  `;

  const invertImage = css`
    filter: invert(${theme.name === "light" ? 1 : 0});
  `;

  const noMargin = css`
    margin: 0 !important;
  `;

  const caseStudyCard = css`
    padding: 32px 24px;
    border-radius: 16px;
    height: 100%;

    background: ${themeSwitcher(theme, {
      light: colorsLight.NeutralBgDefault,
      dark: colorsDark.NeutralBgDefault,
    })};

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 32px;

    ${mediaQueries.md} {
      padding: 40px 32px;
    }

    ${mediaQueries.xl} {
      padding: 48px 40px;
      border-radius: 32px;
    }

    ${theme.name === "light" ? `
      box-shadow: ${dropShadows.small};
    ` : ``};

    img {
      height: 42px;

      ${mediaQueries.md} {
        height: 56px;
      }
    }

    a {
      margin-top: auto;
    }
  `;
  
  const logoMargin = css`
    margin-bottom: 80px;

    ${mediaQueries.md} {
      margin-bottom: 96px;
    }

    ${mediaQueries.xl} {
      margin-bottom: 160px;
    }
  `;

  const headingSpacing = css`
    margin-bottom: ${spacing.mobile.l}px;

    ${mediaQueries.md} {
      margin-bottom: ${spacing.tablet.l}px;
    }

    ${mediaQueries.xl} {
      margin-bottom: ${spacing.desktop.l}px;
    }
  `;

  const cardMargin = css`
    margin-bottom: 24px;

    ${mediaQueries.md} {
      margin-bottom: 40px;
    }

    ${mediaQueries.xl} {
      margin-bottom: 0;
    }
  `;

  const quoteMargin = css`
    margin-bottom: 64px;

    ${mediaQueries.md} {
      margin-bottom: 96px;
    }

    ${mediaQueries.lg} {
      margin-bottom: 32px;
    }

    ${mediaQueries.xl} {
      margin-bottom: 120px;
    }
  `;

  const greyOut = css`
    color: ${theme.name === "light" ? colorsLight.NeutralIconWeakDefault : colorsDark.NeutralIconWeakDefault};
    img {
      opacity: 0.75;
    }
  `;

  const upsunCard = css`
    img {
      width: auto;
      height: 35px;
      object-fit: scale-down;
    }

    margin-bottom: 32px;

    ${mediaQueries.md} {
      margin-bottom: 40px;
    }

    ${mediaQueries.xl} {
      margin-bottom: 48px;
    }
  `;

  const slidingCardsMargin = css`
    margin-bottom: 24px;

    ${mediaQueries.md} {
      margin-bottom: 0;
    }
  `;

  const stacksMarginBottom = css`
    margin-bottom: ${spacing.mobile["4xl"]}px;

    ${mediaQueries.md} {
      margin-bottom: ${spacing.tablet["4xl"]}px;
    }

    ${mediaQueries.xl} {
      margin-bottom: ${spacing.desktop["4xl"]}px;
    }
  `;

  const marginTopStacks = css`
    margin-bottom: 24px;
    margin-top: 128px;

    ${mediaQueries.md} {
      margin-top: 160px;
    }

    ${mediaQueries.xl} {
      margin-top: 248px;
    }
  `;

  const maxWidth = css`
    max-width: 1000px;
    margin: auto;
  `;

  const caseStudyImg = css`
    width: 224px;
    height: 56px;
    object-fit: scale-down;
    object-position: left;
  `;

  const trustedBannerStyles = css`
    ${respSpacing('margin-block','4xl')}
  `;

  const localizationLinks = [
    { text: "English", href: "/" },
    { text: "Français", href: "/fr/" },
    { text: "Deutsch", href: "/de/" },
  ];

  return (
    <Layout cta localizations={localizationLinks} location={location.pathname} locale="en">
      <HomepageHero />
      <div css={logoMargin}>
        <LogoShowcase
          items={homepageLogos}
          speed={"120s"}
          headline="Trusted by organizations and development teams worldwide"
          rows={2}
        />
      </div>
      <GridContainer>
        <GridCol colStart={1} colEnd={13} css={[center, headingSpacing]}>
          <Heading
            level={2}
            text={"Simplify turning code into secure production applications"}
            css={maxWidth}
          />
        </GridCol>
      </GridContainer>
      <InfoItemsHomepage
        data={[
          {
            title: "Preview environments, including all your data",
            subtitle: "Clone production to preview environments instantly. Get exact copies of configuration, code, and data to minimize release risk and ensure deployments behave as expected.",
            lottie: "/homepage/animations/speed-flexibility-no-looping.lottie",
            video: "/homepage/animations/speed-flexibility-no-looping",
            learnMore: "https://docs.upsun.com/administration/web/configure-environment.html#actions-on-environments"
          },
          {
            title: "Collaboration, simplified",
            subtitle:
              "Simultaneously develop features, share feedback with stakeholders, and iterate faster without IT dependencies.",
            lottie: "/homepage/animations/collaboration-no-looping.lottie",
            video: "/homepage/animations/collaboration-no-looping",
          },
          {
            title: "Complete observability",
            subtitle:
              "Proactively address performance issues, optimize resource usage, and gain insights for swift responses to events.",
            lottie: "/homepage/animations/observability-no-looping.lottie",
            video: "/homepage/animations/observability-no-looping",
          },
        ]}
        orientation={"right"}
      />
      <GridContainer>
        <GridCol colStart={1} colEnd={13} css={[center, headingSpacing]}>
          <Heading
            level={2}
            text={
              "Recognized by Gartner® in the 2025 Magic Quadrant™ for Cloud-Native Application Platforms: discover why leading teams are building on Upsun."
            }
            css={maxWidth}
          />
        </GridCol>
      </GridContainer>
      <InfoItemsHomepage
        data={[
          {
            title: "Reliable infrastructure, 24x7 support",
            subtitle:
              "Automate compliance (SOC 2, PCI DSS Level 1) and daily backups on a reliable infrastructure with expert global support.",
            lottie: "/homepage/animations/reliable-infrastructure-no-looping.lottie",
            video: "/homepage/animations/reliable-infrastructure-no-looping"
          },
          {
            title: "Efficient development and cost control",
            subtitle:
              "Automate provisioning and optimize resources with container-level CPU and RAM controls to reduce project costs.",
            lottie: "/homepage/animations/cost-control-no-looping.lottie",
            video: "/homepage/animations/cost-control-no-looping"
          },
          {
            title: "Flexible and future-proof technology",
            subtitle:
              "Use multiple languages, customize stacks site-by-site, and ensure 100% CLI compatibility for smooth workflows.",
            lottie: "/homepage/animations/flexible-future-proof.lottie",
            video: "/homepage/animations/flexible-future-proof"
          },
          {
            title: "Sustainable Cloud Hosting, Greenly certified",
            subtitle:
              "Up to 12x more efficient CPU usage,¹ 15x lower emissions in low-carbon regions,² certified by B Labs ™ and Greenly-verified.",
            lottie: "/homepage/animations/sustainable.lottie",
            video: "/homepage/animations/sustainable"
          },
        ]}
        orientation={"left"}
      />
      <TrustedBanner 
        title="Trusted by developers and teams worldwide"
        subtitle="A bright, new offering powered by Platform.sh—adopted (and ❤️) by 17,000+ developers, over the last 8 years."
        styles={trustedBannerStyles}
      />
      <GridContainer>
        <GridCol colStart={1} colEnd={13} css={[center, headingSpacing]}>
          <Heading level={2} text={"What success looks like with Upsun"} />
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartXl={2}
          colEndXl={7}
          css={cardMargin}
        >
          <div css={[card, gradientBorder]}>
            <div css={upsunCard}>
              <p css={noMargin}>With</p>
              <img src="/logo.svg" css={invertImage} alt="Upsun"/>
            </div>
            <FeatureList
              style="check"
              items={[
                {
                  title: "Faster deployments",
                  description:
                    "Reduce deployment time by up to 50% with instant, real-time environments.",
                },
                {
                  title: "Lower costs",
                  description:
                    "Scale efficiently with predictable pricing and avoid overpaying for unused resources.",
                },
                {
                  title: "Improved collaboration",
                  description:
                    "Keep teams in sync across front-end, back-end, and APIs, reducing bottlenecks and miscommunication.",
                },
                {
                  title: "Stronger security",
                  description:
                    "Protect your collaboration with SSO and MFA , ensuring that your environments are always secure.",
                },
              ]}
            />
          </div>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartXl={7}
          colEndXl={12}
        >
          <div css={[card, greyBorder]}>
            <div css={[upsunCard, greyOut]}>
              <p css={noMargin}>Without</p>
              <img src="/logo.svg" css={invertImage} alt="Upsun"/>
            </div>
            <FeatureList
              style="cross"
              items={[
                {
                  title: "Slow development cycles",
                  description:
                    "Lose valuable time to manual environment setups, leading to missed deadlines and slower time-to-market.",
                },
                {
                  title: "Wasted resources",
                  description:
                    "Overpay for cloud infrastructure you don't fully use, driving up unnecessary costs.",
                },
                {
                  title: "Inefficient collaboration",
                  description:
                    "Teams remain siloed, leading to misaligned work streams and more errors in production.",
                },
                {
                  title: "Increased security risks",
                  description:
                    "Without built-in security features like SSO and MFA , your workflows are more vulnerable to breaches.",
                },
              ]}
            />
          </div>
        </GridCol>
        <GridCol colStart={1} colEnd={13} colStartMd={2} colEndMd={12} css={quoteMargin}>
          <DesaturatedQuote attr="Lukas Kahwe Smith, CTO and Co-founder, Witty Works">
            <>
              Anyone who has some level of knowledge <span>about DevOps</span>{" "}
              concepts and <span>YAML syntax</span> can get started with Upsun;
              it’s a fairly easy product for the amount of complexity it can
              handle. That’s the magic.
            </>
          </DesaturatedQuote>
        </GridCol>
        <GridCol colStart={1} colEnd={13} colEndMd={7} colStartXl={2} css={slidingCardsMargin}>
          <div css={[card, gradientBorderTransform]} className={"slideUp"}>
            <p>
              Get instant access to real-time environments and see how Upsun can
              transform your workflows.
            </p>
            <Button level="primary" text="Start your free trial" href="https://auth.upsun.com/register/" />
          </div>
        </GridCol>
        <GridCol colStart={1} colEnd={13} colStartMd={7} colEndXl={12}>
          <div css={[card, greyBorder]} className={"slideDown"}>
            <p>
              See how Upsun can meet your team's specific needs. Book a demo and
              get a tailored walkthrough.
            </p>
            <Button level="secondary" text="Talk to sales" href="/contact-us/" />
          </div>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          css={[
            center,
            marginTopStacks
          ]}
        >
          <Heading level={2}>
            Polyglot? Hyperpolyglot?
            <br />
            We glot you.
          </Heading>
          <p css={maxWidth}>
            Upsun supports an array of cool languages and frameworks. Monoliths.
            Microservies. Multistack. So you can keep your options open.
            Flexible. And future-proof.
          </p>
        </GridCol>
        <GridCol colStart={1} colEnd={13} css={stacksMarginBottom}>
          <Stacks />
        </GridCol>
        <GridCol colStart={1} colEnd={13} css={[center, headingSpacing]}>
          <Heading level={2} text={"Real-world success stories"} />
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartMd={"auto"}
          colEndMd={"span 6"}
          colStartLg={"auto"}
          colEndLg={"span 4"}
          css={cardMargin}
        >
          <div css={caseStudyCard}>
          <img src={theme.name === "light" ? unicefLight : unicefDark}  alt="UNICEF" css={caseStudyImg}/>
            <Heading
              level={4}
              text={
                "How UNICEF cut costs by 60% and scaled globally with Upsun"
              }
            />
            <Button text="Read the case study" href="/blog/unicef-switzerland-and-liechtenstein/" level="tertiary" />
          </div>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartMd={"auto"}
          colEndMd={"span 6"}
          colStartLg={"auto"}
          colEndLg={"span 4"}
          css={cardMargin}
        >
          <div css={caseStudyCard}>
          <img src={theme.name === "light" ? mizzouLight : mizzouDark}  alt="Mizzou" css={caseStudyImg}/>
            <Heading
              level={4}
              text={
                "University of Missouri cuts website costs by 30%, reduces maintenance by 75%, and boosts performance by 300%"
              }
            />
            <Button text="Read the case study" href="/blog/university-of-missouri/" level="tertiary" />
          </div>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartMd={"auto"}
          colEndMd={"span 6"}
          colStartLg={"auto"}
          colEndLg={"span 4"}
          css={cardMargin}
        >
          <div css={caseStudyCard}>
          <img src={theme.name === "light" ? freitagLight : freitagDark} alt="Freitag" css={caseStudyImg}/>
            <Heading
              level={4}
              text={
                "Freitag boosts holiday traffic stability and lifts conversions by 25%"
              }
            />
            <Button text="Read the case study" href="/blog/freitag-drupal-commerce-upcycling/" level="tertiary" />
          </div>
        </GridCol>
      </GridContainer>
    </Layout>
  );
};

export const Head: HeadFC = ({ location }) => {
  const description =
    "Upsun provides ultimate developer flexibility with self-service, predictable pricing. Customize your resources, runtimes, environments, and more. Start your free trial today!";

  const translations = [
    {
      locale: "en",
      url: "/",
    },
    {
      locale: "fr",
      url: "/fr/",
    },
    {
      locale: "de",
      url: "/de/",
    }
  ];

  return (
    <SEO
      title={"Upsun: a highly flexible PaaS to develop applications"}
      description={description}
      pathname={location.pathname}
      translations={translations}
      locale={"en"}
    />
  );
};

export default IndexPage;
